import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { formatJoinCode } from '../../lib/joinCode';
import type { GroupRole, Invite, JoinCodeInfo } from '../../lib/types';
import { useStore } from '../../store/useStore';
import { ErrorLine, Section } from './ui';

/** The two ways somebody gets into the team: the shared code, or a direct invite. */
export function JoiningTab() {
  return (
    <>
      <JoinCodeSection />
      <InvitesSection />
    </>
  );
}

// --- Join code --------------------------------------------------------------

function JoinCodeSection() {
  const groupId = useStore((s) => s.activeGroupId);

  const [info, setInfo] = useState<JoinCodeInfo | null>(null);
  const [copied, setCopied] = useState<'code' | 'link' | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setInfo(await api<JoinCodeInfo>(`/api/groups/${groupId}/join-code`));
  }, [groupId]);

  useEffect(() => {
    void load().catch(() => undefined);
  }, [load]);

  async function update(body: { rotate?: boolean; isActive?: boolean }) {
    setBusy(true);
    setError(null);
    try {
      setInfo(await api<JoinCodeInfo>(`/api/groups/${groupId}/join-code`, { method: 'PATCH', body }));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not update the join code');
    } finally {
      setBusy(false);
    }
  }

  function copy(what: 'code' | 'link', value: string) {
    void navigator.clipboard.writeText(value).catch(() => undefined);
    setCopied(what);
    setTimeout(() => setCopied(null), 1600);
  }

  if (!info) return null;

  return (
    <Section
      title="Join code"
      description="Anyone with this code can add themselves to the team as a member. Rotate it to cut off everyone who has the old one."
    >
      <div className="card flex flex-wrap items-center gap-3 p-4">
        <span
          className={`size-2 shrink-0 rounded-full ${info.isActive ? 'bg-done' : 'bg-todo'}`}
          title={info.isActive ? 'Open' : 'Closed'}
        />
        <code
          className={`font-mono text-lg tracking-widest ${
            info.isActive ? 'text-ink' : 'text-ink-faint line-through'
          }`}
        >
          {formatJoinCode(info.joinCode)}
        </code>

        <span className="ml-auto flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => copy('code', info.joinCode)}
            className="btn btn-ghost !py-1 !text-xs"
          >
            {copied === 'code' ? 'Copied' : 'Copy code'}
          </button>
          <button
            type="button"
            onClick={() => copy('link', info.joinUrl)}
            className="btn btn-ghost !py-1 !text-xs"
          >
            {copied === 'link' ? 'Copied' : 'Copy link'}
          </button>
          <button
            type="button"
            onClick={() => void update({ isActive: !info.isActive })}
            disabled={busy}
            className="btn btn-ghost !py-1 !text-xs"
          >
            {info.isActive ? 'Close joining' : 'Open joining'}
          </button>
          <button
            type="button"
            onClick={() => void update({ rotate: true })}
            disabled={busy}
            className="btn btn-danger !py-1 !text-xs"
          >
            Rotate
          </button>
        </span>
      </div>

      <p className="text-xs text-ink-faint">
        {info.isActive
          ? 'People who join this way land in no dev field, so they see nothing until you add them to one above.'
          : 'Joining by code is off. Only invite links work right now.'}
      </p>

      <ErrorLine message={error} />
    </Section>
  );
}

// --- Invites ----------------------------------------------------------------

function InvitesSection() {
  const groupId = useStore((s) => s.activeGroupId);

  const [invites, setInvites] = useState<Invite[]>([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<GroupRole>('member');
  const [issued, setIssued] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const result = await api<{ invites: Invite[] }>(`/api/groups/${groupId}/invites`);
    setInvites(result.invites);
  }, [groupId]);

  useEffect(() => {
    void load().catch(() => undefined);
  }, [load]);

  async function create(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setIssued(null);
    try {
      const result = await api<{ inviteUrl: string }>(`/api/groups/${groupId}/invites`, {
        method: 'POST',
        body: { email, role },
      });
      setIssued(result.inviteUrl);
      setEmail('');
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not create the invite');
    }
  }

  async function revoke(inviteId: string) {
    await api(`/api/groups/${groupId}/invites/${inviteId}`, { method: 'DELETE' });
    await load();
  }

  return (
    <Section
      title="Invites"
      description="For inviting one person directly: the link works once, for that email address only."
    >
      <form onSubmit={create} className="flex flex-wrap gap-2">
        <input
          className="field-input min-w-48 flex-1 text-sm"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="teammate@example.com"
          required
        />
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as GroupRole)}
          className="field-input !w-auto text-sm"
          aria-label="Role for the invited person"
        >
          <option value="member">Member</option>
          <option value="admin">Admin</option>
        </select>
        <button type="submit" className="btn btn-ghost">
          Create invite
        </button>
      </form>

      {issued && (
        <div className="animate-rise card flex items-center gap-2 p-3">
          <code className="min-w-0 flex-1 truncate font-mono text-xs text-ink-soft">{issued}</code>
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard.writeText(issued).catch(() => undefined);
              setCopied(true);
              setTimeout(() => setCopied(false), 1600);
            }}
            className="btn btn-primary !py-1 !text-xs"
          >
            {copied ? 'Copied' : 'Copy link'}
          </button>
        </div>
      )}
      {issued && (
        <p className="text-xs text-ink-faint">
          Send this link to them yourself — it is shown once and cannot be retrieved later.
        </p>
      )}

      {invites.length > 0 && (
        <div className="flex flex-col gap-2">
          {invites.map((invite) => (
            <div key={invite.id} className="card flex items-center gap-3 p-3">
              <span className="truncate text-sm">{invite.email}</span>
              <span className="text-xs text-ink-faint capitalize">{invite.role}</span>
              <span className="ml-auto flex shrink-0 items-center gap-3">
                <span className="text-xs text-ink-faint">
                  expires {invite.expiresAt.slice(0, 10)}
                </span>
                <button
                  type="button"
                  onClick={() => void revoke(invite.id)}
                  className="btn btn-danger !py-1 !text-xs"
                >
                  Revoke
                </button>
              </span>
            </div>
          ))}
        </div>
      )}

      <ErrorLine message={error} />
    </Section>
  );
}
